import React, { useEffect, useState } from "react";
import { useRef } from "react";
import "./CustomDropdownStyle.css";
import { Text } from "../../MakersV2/Theme/Typography";
import { AngleDown } from "../../MakersV2/Theme/Icons";

const CustomDropdown = ({
  dropName,
  options,
  // isOpen,
  // setIsActive,
  disabled,
  className,
  label,
  isimp,
  minWidth,
  maxWidth,
  onSelect,
  // setSelectedOption,
  selectedOptionName,
  type,
  field, 
  isError,
  erroMessage,
  isMultiSelect = false, // New prop for multi-select
  searchable = false, // New prop for search functionality
  selectedOptions = [], // New prop for pre-selected options in multi-select mode
}) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedOptionsList, setSelectedOptionsList] = useState(selectedOptions || []);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Debug: Log initial props
  useEffect(() => {
    if (isMultiSelect) {
      console.log('CustomDropdown: Initial props', {
        selectedOptions,
        selectedOptionsList,
        optionsLength: options?.length
      });
    }
  }, []);

  const handleToggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };
  // const handleOptionClick = (option) => {
  //   setSelectedOption(option);
  //   setIsActive(false);
  // };

  const handleSelectOption = (option) => {
    if (isMultiSelect) {
      // Multi-select mode
      const isSelected = selectedOptionsList.some((item) => {
        // If both have id and they match
        if (item.id !== undefined && option.id !== undefined && item.id === option.id) {
          return true;
        }
        // If both have value and they match
        if (item.value !== undefined && option.value !== undefined && item.value === option.value) {
          return true;
        }
        // If both have name and they match (fallback)
        if (item.name !== undefined && option.name !== undefined && item.name === option.name) {
          return true;
        }
        return false;
      });
      
      let updatedOptions;
      if (isSelected) {
        // Remove from selection
        updatedOptions = selectedOptionsList.filter((item) => {
          // If both have id and they match, exclude
          if (item.id !== undefined && option.id !== undefined && item.id === option.id) {
            return false;
          }
          // If both have value and they match, exclude
          if (item.value !== undefined && option.value !== undefined && item.value === option.value) {
            return false;
          }
          // If both have name and they match, exclude (fallback)
          if (item.name !== undefined && option.name !== undefined && item.name === option.name) {
            return false;
          }
          return true;
        });
      } else {
        // Add to selection
        updatedOptions = [...selectedOptionsList, option];
      }
      
      setSelectedOptionsList(updatedOptions);
      onSelect(updatedOptions, field);
      // Don't close dropdown in multi-select mode
    } else {
      // Single select mode
      setSelectedOption(option);
      onSelect(option, field);
      setIsOpen(false);
    }
  };

  // Filter options based on search query
  const filteredOptions = searchable && searchQuery
    ? options.filter((option) =>
        option.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        option.value?.toString().toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options;

  // Check if option is selected (for multi-select)
  const isOptionSelected = (option) => {
    if (!isMultiSelect || !selectedOptionsList || selectedOptionsList.length === 0) return false;
    
    // Use a more robust comparison that handles undefined/null values
    return selectedOptionsList.some((item) => {
      // If both have id and they match
      if (item.id !== undefined && option.id !== undefined && item.id === option.id) {
        return true;
      }
      // If both have value and they match
      if (item.value !== undefined && option.value !== undefined && item.value === option.value) {
        return true;
      }
      // If both have name and they match (fallback)
      if (item.name !== undefined && option.name !== undefined && item.name === option.name) {
        return true;
      }
      return false;
    });
  };

  // Get display text for selected options
  const getDisplayText = () => {
    if (isMultiSelect) {
      if (selectedOptionsList.length === 0) {
        return dropName ?? "Select options";
      } else if (selectedOptionsList.length === 1) {
        return selectedOptionsList[0].name;
      } else {
        return `${selectedOptionsList.length} selected`;
      }
    } else {
      return selectedOption
        ? selectedOption.name
        : dropName ?? "Select an option";
    }
  };

  // console.log("printing first data", selectedOptionName);

  useEffect(() => {
    if (type === "program" || type === "batch") {
      // console.log("printing program option", options);
      // console.log("selected option name as well", selectedOptionName);

      // Find the option with the provided name and set it as the initially selected option
      const initialOption = options.find(
        (option) => option.id === selectedOptionName
      );

      // console.log("printing initial option", initialOption);

      if (initialOption) {
        setSelectedOption(initialOption);
      }
    }

    if (type === "programList" || type === "batchList") {
      // console.log("printing program option", options);
      // console.log("selected option name as well", selectedOptionName);

      // Find the option with the provided name and set it as the initially selected option
      const initialOption = options.find(
        (option) => option.id === selectedOptionName
      );

      // console.log("printing initial option", initialOption);

      if (initialOption) {
        setSelectedOption(initialOption);
      }
    }

    // For school (and any option list keyed by value), match by option.value so dropdown auto-fills
    if (type === "school" && selectedOptionName != null && options?.length) {
      const initialOption = options.find(
        (option) => option.value === selectedOptionName
      );
      if (initialOption) {
        setSelectedOption(initialOption);
      }
    }
  }, [options, selectedOptionName, type]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery(""); // Clear search when closing
      }
    };

    window.addEventListener("click", handleClickOutside);

    return () => {
      window.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  // Update selectedOptionsList when prop changes (only if it's actually different)
  useEffect(() => {
    if (isMultiSelect) {
      // Use functional update to compare with current state
      setSelectedOptionsList(prevList => {
        // Handle case where selectedOptions is not an array
        if (!selectedOptions || !Array.isArray(selectedOptions)) {
          if (prevList.length > 0) {
            console.log('CustomDropdown: Resetting selectedOptionsList - invalid prop', selectedOptions);
            return [];
          }
          return prevList;
        }
        
        // Create a deep comparison function
        const arraysEqual = (arr1, arr2) => {
          if (!arr1 && !arr2) return true;
          if (!arr1 || !arr2) return false;
          if (arr1.length !== arr2.length) return false;
          const ids1 = arr1.map(item => item?.id ?? item?.value ?? item?.name).filter(Boolean).sort().join(',');
          const ids2 = arr2.map(item => item?.id ?? item?.value ?? item?.name).filter(Boolean).sort().join(',');
          return ids1 === ids2;
        };
        
        // Only update if the arrays are actually different
        if (!arraysEqual(prevList, selectedOptions)) {
          console.log('CustomDropdown: Updating selectedOptionsList', {
            prevList,
            newList: selectedOptions,
            prevIds: prevList?.map(item => item?.id ?? item?.value ?? item?.name),
            newIds: selectedOptions?.map(item => item?.id ?? item?.value ?? item?.name)
          });
          return selectedOptions;
        }
        return prevList;
      });
    }
  }, [isMultiSelect, selectedOptions]);

  return (
    <div
      ref={dropdownRef}
      className={`customDropdownWrap flexbox ${
        isOpen && "active"
      } ${className}`}
      //   onClick={() => {
      //     if (!disabled) {
      //       setIsActive((prev) => !prev);
      //     }
      //   }

      // }
    >
      {label && (
        <Text font14 inter regular neutral13>
          {label} {isimp && <span className="impRed">*</span>}
        </Text>
      )}
      <div className="dropDrop flexbox" style={{minWidth: minWidth}} onClick={() => handleToggleDropdown()}>
        <div className="dropText">
          {getDisplayText()}
        </div>
        {/* <img src="/assets/svg/arrow-down.svg" alt="" /> */}
        {type !== "program" && type !== "batch" && (
          <AngleDown 
            className={isOpen ? "dropdownArrow rotated" : "dropdownArrow"}
          />
        )}
      </div>
      {/* <div className="dropMenuWindow">
        {options?.map((item, index) => (
          <label className="dropMenu" key={index} onClick={item.action}>
            <input type="radio" name="radio" />
            <span className="menuList">{item.name}</span>
          </label>
        ))}
      </div> */}
      {/* TODO : undefined for other pages */}
            {isError && <span className="FormError">{erroMessage}</span>}

      <div className="dropMenuWindow">
        {searchable && (
          <div className="dropdownSearchContainer">
            <input
              ref={searchInputRef}
              type="text"
              className="dropdownSearchInput"
              placeholder="Search options..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
        <div className="dropdownOptionsContainer">
          {filteredOptions?.length > 0 ? (
            filteredOptions.map((item, index) => (
              <label
                className={`dropMenu ${isOptionSelected(item) ? "selected" : ""}`}
                key={item.id || item.value || index}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectOption(item);
                }}
              >
                <input
                  type={isMultiSelect ? "checkbox" : "radio"}
                  name={isMultiSelect ? `checkbox-${field || "multi"}` : "radio"}
                  checked={isMultiSelect ? isOptionSelected(item) : false}
                  readOnly
                />
                <span className="menuList">{item.name}</span>
              </label>
            ))
          ) : (
            <div className="dropdownNoResults">No options found</div>
          )}
        </div>
        {isMultiSelect && selectedOptionsList.length > 0 && (
          <div className="dropdownSelectedCount">
            {selectedOptionsList.length} selected
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomDropdown;
